import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import { Flame, ChevronRight } from "lucide-react";
import { api } from "@/lib/api";
import { useLeft } from "@/lib/quest";
import { QuestFinale } from "@/components/quest/QuestFinale";
import { DoneStamp } from "@/components/quest/DoneStamp";

export function useQuestLive() {
  const [q, setQ] = useState(null);
  useEffect(() => {
    const load = () => api.get("/quest/live").then(({ data }) => setQ(data)).catch(() => {});
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, []);
  return q === null ? undefined : (q.running ? q : null);
}

const Left = ({ end }) => {
  const { left, text } = useLeft(end);
  return left > 0 ? <span className="hidden sm:inline tabular-nums opacity-80" data-testid="quest-bar-timer">· noch {text}</span> : null;
};

const COLORS = ["#FACC15", "#FDE68A", "#F59E0B", "#FFFFFF", "#F43F5E", "#FB923C"];

function MilestoneConfetti({ pct }) {
  const prev = useRef(null);
  const [hit, setHit] = useState(0);
  useEffect(() => {
    const m = [90, 50].find((x) => pct >= x && prev.current !== null && prev.current < x);
    prev.current = pct;
    if (!m) return undefined;
    setHit(m);
    const t = setTimeout(() => setHit(0), 4200);
    return () => clearTimeout(t);
  }, [pct]);
  if (!hit) return null;
  return (
    <div className="absolute inset-x-0 top-0 h-72 z-[60] pointer-events-none overflow-hidden" data-testid="quest-milestone-confetti" aria-hidden>
      {Array.from({ length: 60 }, (_, i) => (
        <span key={i} className="ms-confetti" style={{ left: `${(i * 37) % 100}%`, background: COLORS[i % COLORS.length], animationDelay: `${(i % 15) * 0.09}s`, animationDuration: `${1.8 + (i % 5) * 0.3}s`, width: i % 3 ? 8 : 6, height: i % 3 ? 12 : 6 }} />
      ))}
      <span className="absolute left-1/2 top-12 -translate-x-1/2 ms-pop px-4 h-9 inline-flex items-center rounded-full bg-[#4a2a08] text-amber-200 font-black text-sm shadow-xl whitespace-nowrap" data-testid="quest-milestone-label">{hit} % geschafft! 🎉</span>
    </div>
  );
}

function Finale({ pct, end }) {
  const key = `quest_finale_${end}`;
  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (pct >= 100 && !localStorage.getItem(key)) { localStorage.setItem(key, "1"); setOpen(true); }
  }, [pct, key]);
  return open ? createPortal(<QuestFinale end={end} onClose={() => setOpen(false)} />, document.body) : null;
}

export const QuestEventBar = ({ q }) => !q ? null : (
  <div className="relative">
  <Link to="/community-aufgabe" className="gold-bar h-9 flex items-center justify-center gap-1.5 sm:gap-2 px-2 sm:px-3 text-[#4a2a08] font-black text-[10px] sm:text-[13px] uppercase tracking-normal sm:tracking-wider" data-testid="quest-event-bar">
    <Flame className="w-4 h-4 shrink-0 gold-bar-flicker" />
    <span className="truncate" data-testid="quest-event-bar-text">Wochenend-Event aktiv: Community-Aufgabe</span>
    <span className="shrink-0 inline-flex items-center gap-1.5" data-testid="quest-event-bar-progress">
      <span className="hidden sm:block w-20 h-2 rounded-full bg-[#4a2a08]/20 overflow-hidden"><span className="block h-full rounded-full bg-[#4a2a08] transition-[width] duration-700" style={{ width: `${q.pct}%` }} /></span>
      <span className="px-1.5 h-5 inline-flex items-center rounded-full bg-[#4a2a08] text-amber-200 text-[10px] sm:text-[11px] tabular-nums" data-testid="quest-event-bar-pct">{q.pct} %</span>
    </span>
    {q.done ? <DoneStamp small testId="quest-bar-done-stamp" /> : <Left end={q.end} />}
    <ChevronRight className="w-4 h-4 shrink-0" />
  </Link>
  <MilestoneConfetti pct={q.pct} />
  <Finale pct={q.pct} end={q.end} />
  </div>
);

export const QuestEventBarSpacer = ({ q }) => (q ? <div className="h-9" /> : null);
